/* ============================================================================
   COOPER PROJECT — THE CART DRAWER
   ============================================================================
   Companion to sections/cooper-cart.liquid. Read that file's header first: it
   records where the composition came from and which parts of the reference were
   copied deliberately and which were not.

   WHAT THIS TALKS TO. Shopify's Cart AJAX API, and nothing else:
     GET  /cart.js                          read the cart
     POST /cart/add.js                      add a variant
     POST /cart/change.js  {line,quantity}  change or remove a line
     GET  /recommendations/products.json    fill the upsell
   Every response is JSON, so nothing here depends on Shopify's Section
   Rendering API and nothing re-parses HTML the server built.

   WHY BY LINE NUMBER AND NOT BY VARIANT ID. change.js takes either. It has to
   be `line` here: two lines can hold the SAME variant with different line-item
   properties — a bucket hat with "GOOD BOY" stitched on it and an identical one
   with "BAD DOG" — and by id the API would change whichever it found first.
   That is not hypothetical in this shop; the line is the product.

   THE ROUTE PREFIX IS NOT ASSUMED. A shop with markets or a language prefix
   serves /fr/cart.js, and hardcoding /cart.js there quietly 404s. Shopify
   publishes the correct roots in `window.Shopify.routes.root`.
   ========================================================================= */
(function () {
  'use strict';

  var root = document.querySelector('[data-cooper-cart]');
  if (!root) return;

  var ROOT_URL = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var url = function (path) {
    return ROOT_URL.replace(/\/$/, '') + path;
  };

  var panel = root.querySelector('[data-cart-panel]');
  var itemsEl = root.querySelector('[data-cart-items]');
  var emptyEl = root.querySelector('[data-cart-empty]');
  var footEl = root.querySelector('[data-cart-foot]');
  var countEl = root.querySelector('[data-cart-count]');
  var subtotalEl = root.querySelector('[data-cart-subtotal]');
  var savedEl = root.querySelector('[data-cart-saved]');
  var checkoutBtn = root.querySelector('[data-cart-checkout]');
  var checkoutLabel = root.querySelector('[data-cart-checkout-label]');
  var statusEl = root.querySelector('[data-cart-status]');
  var errorEl = root.querySelector('[data-cart-error]');
  var rewardEl = root.querySelector('[data-cart-reward]');
  var rewardText = root.querySelector('[data-cart-reward-text]');
  var barEl = root.querySelector('[data-cart-bar]');
  var barWrap = root.querySelector('[data-cart-bar-wrap]');
  var upsellEl = root.querySelector('[data-cart-upsell]');
  var upsellList = root.querySelector('[data-cart-upsell-list]');

  var MONEY_FORMAT = root.dataset.moneyFormat || '{{amount}}';

  /* THE THRESHOLD IS TYPED IN THE SHOP'S CURRENCY AND THE CART IS NOT.
     Until markets existed these were the same thing, and the comment above
     paintReward still says so. They are not: `cart.total_price` comes back in
     the PRESENTMENT currency of the market the visitor is in, while
     `section.settings.free_shipping_threshold` is a euro figure typed once.
     Subtracting one from the other made the drawer read the €40 threshold as
     $40 — and $40 is LOWER than €40, so the bar announced free shipping the
     till would still charge for. That is the exact promise this file's own
     header forbids.

     Shopify publishes the market's rate on the page as `Shopify.currency.rate`
     (a string, shop currency → presentment). Missing or unparseable means we
     are in the shop's own currency, so the safe default is 1. */
  var RATE = (function () {
    try {
      var r = parseFloat(window.Shopify && window.Shopify.currency && window.Shopify.currency.rate);
      return isFinite(r) && r > 0 ? r : 1;
    } catch (e) { return 1; }
  })();
  /* AND THE CONVERTED FIGURE IS ROUNDED THE SAME WAY THE ANNOUNCEMENT BAR
     ROUNDS IT. Measured 28.09 on /cs: the bar promised «Doprava zdarma nad
     850 Kč» while this file worked from 34 x 24.87168 = 845,64, so the drawer
     told a buyer with 500 Kč in it that 345,64 Kč were missing. Two figures for
     one promise, and the one with the decimals is the one that looks like a
     mistake.

     snippets/global-script.liquid already publishes the rounding the bar uses —
     window.CooperFreeShip.round — so the two surfaces round through ONE
     function rather than two that agree until a rate moves. The guard keeps the
     old behaviour if that snippet is ever absent: an unrounded threshold is
     wrong by a few crowns, a missing one is wrong by everything. */
  var THRESHOLD = (function () {
    var raw = parseFloat(root.dataset.threshold || '0') * RATE;
    if (!isFinite(raw) || raw <= 0) return 0;
    var fs = window.CooperFreeShip;
    var v = fs && typeof fs.round === 'function' ? fs.round(raw) : raw;
    return Math.round(v * 100) || 0;
  })();
  var UPSELL_LIMIT = parseInt(root.dataset.upsellLimit || '0', 10) || 0;
  var UPSELL_INTENT = root.dataset.upsellIntent || 'related';

  /* THE LINES THAT ARE CHARGES RATHER THAN GARMENTS, lower-cased once. From the
     section rather than written in here, because the day a gift-wrap line joins
     the cart the fix should be a setting and not a deploy. */
  var CHARGE_TYPES = (root.dataset.chargeTypes || 'Add-on')
    .split(',')
    .map(function (s) { return s.trim().toLowerCase(); })
    .filter(Boolean);

  var lastFocus = null;
  var busy = false;
  var cart = null;

  /* -------------------------------------------------------------------------
     MONEY.

     Shopify's money_format is a template with one of four tokens in it, and the
     shop's own is `{{amount}} Kč` — comma-less thousands, comma decimal. This is
     a port of the formatter every Shopify theme carries, and it is here rather
     than imported because the theme's own copy lives inside a 1,154-line bundle
     this drawer does not otherwise touch.

     NO Intl.NumberFormat. It formats correctly for a locale and then disagrees
     with the rest of the shop, which prints prices through Liquid's `money`
     filter — and two spellings of the same price in one panel is worse than an
     unfashionable one.
     ---------------------------------------------------------------------- */
  function money(cents) {
    var value = (cents || 0) / 100;

    function group(number, decimals, thousands, decimal) {
      var fixed = Math.abs(number).toFixed(decimals);
      var parts = fixed.split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+$)/g, '$1' + thousands);
      var frac = parts[1] ? decimal + parts[1] : '';
      return whole + frac;
    }

    return MONEY_FORMAT.replace(/\{\{\s*(\w+)\s*\}\}/, function (_, token) {
      switch (token) {
        case 'amount_no_decimals':
          return group(value, 0, ',', '.');
        case 'amount_with_comma_separator':
          return group(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return group(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return group(value, 2, "'", '.');
        /* THE THREE SPACE FORMATS, one of which is the normal Czech spelling of
           a price. They were missing, so they fell to the default and printed
           comma thousands: measured against Shopify's own table over 13 values
           (0, 1, 5, 50, 99, 100, 999, 1000, 99900, 100000, 100001, 4455000,
           123456789), {{amount_with_space_separator}} Kč printed
           '1,234,567.89 Kč' here where Liquid prints '1 234 567,89 Kč', and
           {{amount_no_decimals_with_space_separator}} Kč printed '999.00 Kč'
           where Liquid prints '999 Kč'. Latent today — the shop's format is
           {{amount_with_comma_separator}} Kč, which agrees to the character —
           but one click in Settings, Store details switches it and then every
           Liquid price and every drawer price disagree, which is the exact
           failure the note above says this function exists to prevent.

           THE DECIMAL FOR amount_with_space_separator IS A COMMA, and this is
           the one place where the theme's own JS is not the authority. Ella's
           port at assets/global.js:869 formats it (2, ' ', '.') — a period —
           but Shopify's currency-formatting table gives
           amount_with_space_separator as 1 134,65 and
           amount_with_period_and_space_separator as 1 134.65. Liquid's `money`
           filter is what prints every other price on the page, so Liquid wins;
           following global.js here would have produced 1 234.50 in the drawer
           beside 1 234,50 in the template. */
        case 'amount_with_space_separator':
          return group(value, 2, ' ', ',');
        case 'amount_with_period_and_space_separator':
          return group(value, 2, ' ', '.');
        case 'amount_no_decimals_with_space_separator':
          return group(value, 0, ' ', '.');
        default:
          return group(value, 2, ',', '.');
      }
    });
  }

  /* -------------------------------------------------------------------------
     THUMBNAILS ARE ASKED FOR WITH ?width=, NOT BY MANGLING THE FILENAME.

     The old form — inserting _160x before the extension — is legacy, and it only
     works on a URL that has no size in it already. Measured while shooting the
     drawer: it turned a perfectly good asset url into
     `cooper-shot-buckethat_480x_160x.jpg` and the browser reported
     naturalWidth 0. `?width=` is the current CDN parameter, it composes with a
     url that already carries a query string, and it works for product images
     and theme assets alike.
     ---------------------------------------------------------------------- */
  /* ------------------------------------------------------------------------
     THE GARMENT-ALONE FRAME, AND A CACHE IN FRONT OF IT.

     garmentShot picks the frame out of a product JSON: first the alt the
     import writes on the object-alone shot, then anything that is NOT the
     worn shot, then nothing. Two chances to be right before it gives up, so
     a listing whose alts get rewritten degrades to the featured image rather
     than to a blank square.

     paintGarmentThumbs runs after the drawer is drawn. One fetch per distinct
     handle, remembered in GARMENT for the life of the page: reopening the
     drawer, changing a quantity or adding a second piece costs nothing after
     the first look. A failed lookup is remembered as failed, so a 404 is not
     retried on every redraw.
     --------------------------------------------------------------------- */
  var GARMENT = {};

  function garmentShot(p) {
    /* THE ALTS LIVE IN p.media, NOT IN p.images, and getting that wrong is why
       the first version of this silently did nothing. /products/<handle>.js
       returns `images` as an array of URL STRINGS — measured on amber-cap:
       typeof p.images[0] === 'string' — while `media` is the array of objects
       carrying alt, position and src. Reading .alt off a string yields
       undefined for every frame, so the 'shown on its own' test never matched
       and the not-worn fallback picked frame 0, which IS the worn shot. The
       thumbnail was rewritten to exactly the file it already had.

       p.images stays as the last resort: it has no alts, so it can only ever
       reach the fallback, but a product with media stripped by an app still
       gets a picture rather than a blank. */
    var raw = (p && p.media && p.media.length) ? p.media
      : ((p && p.images && p.images.length) ? p.images : null);
    if (!raw) return '';
    var imgs = raw.map(function (i) {
      return {src: (i && i.src) || i, alt: String((i && i.alt) || '')};
    });
    var own = imgs.filter(function (i) { return /shown on its own/i.test(i.alt); })[0];
    if (own) return own.src;
    var notWorn = imgs.filter(function (i) { return !/\bwearing\b/i.test(i.alt); })[0];
    return notWorn ? notWorn.src : '';
  }

  function paintGarmentThumbs(scope) {
    var nodes = (scope || document).querySelectorAll('[data-cart-thumb]');
    Array.prototype.forEach.call(nodes, function (node) {
      var handle = node.getAttribute('data-cart-thumb');
      if (!handle || node.getAttribute('data-cart-thumb-done')) return;
      var swap = function (product) {
        node.setAttribute('data-cart-thumb-done', '');
        var src = garmentShot(product);
        if (src) node.setAttribute('src', thumb(String(src), 160));
      };
      if (GARMENT[handle] !== undefined) { swap(GARMENT[handle]); return; }
      fetch(url('/products/' + encodeURIComponent(handle) + '.js'), {
        headers: {Accept: 'application/json'}
      })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (product) { GARMENT[handle] = product; swap(product); })
        .catch(function () { GARMENT[handle] = null; node.setAttribute('data-cart-thumb-done', ''); });
    });
  }
  function thumb(src, width) {
    if (!src) return '';
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  /* -------------------------------------------------------------------------
     THE REDUCTION IN THE DRAWER, AND WHY IT HAS TO BE FETCHED.

     Owner, 28.09, with a screenshot of this panel: a bandana at 650 and a hat
     at 625, plain, while every tile and every product page beside them showed
     a struck price and a green saving. The drawer was reading
     `original_line_price`, which is the line BEFORE CART-LEVEL DISCOUNTS -- a
     code, an automatic rule -- and has nothing to do with `compare_at_price`.
     With the two set discounts switched off that figure equals the final one on
     every line, so the <s> never rendered and the footer printed 'You save 0'.

     /cart.js DOES NOT CARRY compare_at_price. Measured on the live cart: the
     line item has price, original_price, line_price, original_line_price,
     final_line_price, total_discount and no anchor of any kind. So it is looked
     up from /products/<handle>.js -- THE SAME CACHE THE THUMBNAILS ALREADY
     FILL, so on a cart whose rows are painted this costs no extra request at
     all, and at worst one small request per distinct product, once.

     BY VARIANT, NOT BY PRODUCT. A hoodie's anchor runs 29 at S to 49 at 5XL;
     reading the product's first variant would strike the same 29 against a
     5XL charged 43 and claim a reduction of minus fourteen. */
  function anchorCents(item) {
    var p = GARMENT[item.handle];
    if (!p || !p.variants) return 0;
    for (var i = 0; i < p.variants.length; i++) {
      if (String(p.variants[i].id) === String(item.variant_id)) {
        var c = p.variants[i].compare_at_price;
        return c && c > p.variants[i].price ? c : 0;
      }
    }
    return 0;
  }

  /* THE LOOKUP IS ASKED FOR ONCE AND THE PANEL IS REPAINTED WHEN IT LANDS.
     paint() stays synchronous -- making it async would put the whole drawer
     behind a network hop, and the rows, the subtotal and the reward bar are all
     correct without the anchor. The anchor is the one thing that arrives late,
     so it arrives late and the panel redraws. */
  var anchorPending = false;
  function ensureAnchors(items) {
    var missing = [];
    for (var i = 0; i < items.length; i++) {
      var h = items[i].handle;
      if (h && GARMENT[h] === undefined && missing.indexOf(h) === -1) missing.push(h);
    }
    if (!missing.length || anchorPending) return;
    anchorPending = true;
    Promise.all(missing.map(function (h) {
      return fetch(url('/products/' + encodeURIComponent(h) + '.js'), {headers: {Accept: 'application/json'}})
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (p) { GARMENT[h] = p; })
        .catch(function () { GARMENT[h] = null; });
    })).then(function () {
      anchorPending = false;
      if (cart) paint();
    });
  }

  function say(message) {
    if (statusEl) statusEl.textContent = message;
  }

  /* -------------------------------------------------------------------------
     A FAILURE HAS TO BE SEEN AS WELL AS ANNOUNCED.

     say() writes into [data-cart-status], which computes position absolute,
     1px by 1px, clip-path inset(50%) - screen-reader only. That was the whole
     bug: with /cart.js forced to a 500 the only honest sentence in the panel
     ('The cart could not be read. Reload the page.') was invisible, while the
     panel went on showing the previous 1 row and subtotal '890,00 Kc' with the
     footer and Checkout button under it. fail() puts the same sentence in
     ordinary flow above the footer as well, so a sighted visitor stops trusting
     the stale figures.
     ---------------------------------------------------------------------- */
  var lastFailure = '';

  function fail(message) {
    lastFailure = message;
    say(message);
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.hidden = false;
    }
  }

  /* THE LIVE REGION IS CLEARED, WHICH IT NEVER USED TO BE. Measured: after a
     forced 500 on /cart.js the next open with a working /cart.js still had
     [data-cart-status] reading 'The cart could not be read. Reload the page.'
     until it was cleared by hand - a screen-reader visitor was handed a failure
     sentence that was no longer true. Called from open() before the read and
     from every successful read. */
  function clearFailure(force) {
    /* THE LIVE REGION IS ONLY BLANKED IF IT STILL HOLDS THE FAILURE, and that
       guard is not defensive programming - without it this swallows somebody
       else's sentence. The submit handler says 'Added.' immediately after
       open(), and the refresh() open() started resolves a moment later; an
       unconditional say('') there would wipe the announcement before a screen
       reader picked it up. `force` is for open(), where the finding is the
       stale-error one and a clean region is the point: a second identical
       message - 'Bandana removed' twice in two visits to the drawer - is not a
       mutation and would never be announced at all. */
    if (force || (lastFailure && statusEl && statusEl.textContent === lastFailure)) say('');
    lastFailure = '';
    if (errorEl) {
      errorEl.textContent = '';
      errorEl.hidden = true;
    }
  }

  var esc = function (s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return {'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'}[c];
    });
  };

  /* -------------------------------------------------------------------------
     OPEN AND CLOSE.

     `inert` ON THE REST OF THE DOCUMENT rather than a modal dialog, and this is
     the lesson from the scratch card written down: showModal() puts an element
     in the top layer, which paints over EVERYTHING — including Shopify's cookie
     banner, which cost a real bug on a phone. inert takes the page out of the
     tab order and out of the pointer's reach without touching paint order, so
     the consent banner stays clickable above this drawer where it belongs.

     THE FILTER TESTS CONTAINMENT, NOT IDENTITY. The bug WAS `el !== root`:
     root is [data-cooper-cart], but sections/cooper-cart.liquid declares
     "tag": "aside", so Shopify wraps the section and root's parent is
     aside#shopify-section-...__cooper_cart — a body child that is NOT root, so
     it collected `inert` and took the entire drawer down with it. Measured with
     the drawer open before the fix: the wrapping ASIDE carried inert, all six
     controls inside reported insideInert true, open()'s own first.focus() left
     document.activeElement at BODY, and document.elementFromPoint at the centre
     of the visible Checkout button returned BODY — nothing in the panel could be
     clicked, tapped or tabbed to. `!el.contains(root)` skips the ancestor
     whatever Shopify wraps it in, and it excludes root itself too, because
     Node.contains counts a node as containing itself.

     AND THE CONSENT BANNER IS SKIPPED BY NAME. section#shopify-pc__banner IS a
     real body child, so the old filter inerted it: z-index 2000000 over this
     drawer's 30, painted and visible, yet elementFromPoint at the centre of its
     Manage preferences button returned BODY and .focus() did not land. That is
     precisely the bug the note above says inert was chosen over showModal() to
     avoid — the drawer took the banner out by a different door. Shopify's
     privacy-consent nodes all carry the `shopify-pc__` id prefix.
     ---------------------------------------------------------------------- */
  function siblings() {
    return Array.prototype.filter.call(document.body.children, function (el) {
      return (!el.contains(root)) &&
        (el.id || '').indexOf('shopify-pc__') !== 0 &&
        el.nodeName !== 'SCRIPT' &&
        el.nodeName !== 'STYLE';
    });
  }

  function open() {
    if (root.dataset.open != null) return;
    lastFocus = document.activeElement;
    root.hidden = false;
    root.setAttribute('aria-hidden', 'false');
    /* Two frames, not one. The panel is translated 100% off-screen while the
       element is display:none; a single frame is not enough for the browser to
       have laid it out, so the transition would not run and it would appear. */
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        root.dataset.open = '';
      });
    });
    siblings().forEach(function (el) {
      el.setAttribute('inert', '');
    });
    document.documentElement.style.overflow = 'hidden';
    document.addEventListener('keydown', onKey);
    var first = panel.querySelector('button:not([tabindex="-1"]), a[href], input, [tabindex]:not([tabindex="-1"])');
    if (first) first.focus();
    clearFailure(true);
    refresh();

    /* THE CAROUSEL IS MEASURED HERE, and this is the third attempt at the same
       bug, so here is the whole chain.

       The upsell is filled by the read that runs at page load, while this panel
       is still `hidden` -- display: none. A display:none element has no
       scrollWidth: it reports 0. So syncNav computed max = 0, hid the nav and
       disabled both arrows, and nothing ever recomputed it, because the only
       other trigger was a scroll event and a disabled button cannot produce one.
       Measured on the live theme: three cards, real max 714, next.disabled true,
       nav hidden.

       Re-running it after two frames (syncNavSoon) did not help either: at page
       load the panel is display:none, frames or no frames. The moment the width
       becomes real is the moment the drawer opens, which is right here. */
    syncNav();
    syncNavSoon();
  }

  function close() {
    if (root.dataset.open == null) return;
    delete root.dataset.open;
    document.removeEventListener('keydown', onKey);
    siblings().forEach(function (el) {
      el.removeAttribute('inert');
    });
    document.documentElement.style.overflow = '';
    root.setAttribute('aria-hidden', 'true');
    var done = function () {
      if (root.dataset.open == null) root.hidden = true;
    };
    /* Wait for the slide out, but never hang on a transitionend that a
       reduced-motion visitor will never get. */
    var moved = false;
    panel.addEventListener('transitionend', function handler(e) {
      if (e.target !== panel || e.propertyName !== 'transform') return;
      panel.removeEventListener('transitionend', handler);
      moved = true;
      done();
    });
    setTimeout(function () {
      if (!moved) done();
    }, 400);
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  function onKey(e) {
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
      return;
    }
    if (e.key !== 'Tab') return;
    /* THE TRAP. aria-modal tells assistive technology the rest is unavailable;
       it does not stop Tab. inert on the siblings does most of the work, but the
       browser's own UI can still walk out of the panel, so the ends are looped
       by hand. */
    var stops = Array.prototype.filter.call(
      panel.querySelectorAll('a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'),
      function (el) {
        return el.offsetParent !== null;
      }
    );
    if (!stops.length) return;
    var first = stops[0];
    var last = stops[stops.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  /* -------------------------------------------------------------------------
     RENDER.
     ---------------------------------------------------------------------- */
  function lineHTML(item, index) {
    /* PROPERTIES WITH A LEADING UNDERSCORE ARE PRIVATE by Shopify's own
       convention — themes and apps use them for machinery — so they are not
       printed. The visitor's stitched line is a public property and is the one
       thing on this row that is not on the product page. */
    var props = '';
    if (item.properties) {
      Object.keys(item.properties).forEach(function (key) {
        var value = item.properties[key];
        if (key.charAt(0) === '_' || value == null || value === '') return;
        props += '<span>' + esc(key) + ': ' + esc(value) + '</span>';
      });
    }

    /* THE CART SHOWS THE GARMENT, NOT THE DOG WEARING IT, and that split is
       deliberate rather than an oversight in one place or the other.

       On 4 September 2026 the lead frame of 49 listings was moved to the
       'A corgi wearing…' shot, because measured at the sizes it actually
       appears in — 161 CSS px on a collection tile, 116 px in a Shopify
       Shopping tile — the garment-alone frame is not recognisable: a cap
       reads as an oven glove, a bandana as a spotted diamond. That fixed the
       ad, the share card, the feed and the collection grid all at once.

       IT BROKE THE CART, and the owner caught it: 'в карте не видно
       продукта'. A 72x90 thumbnail of a dog's head tells the buyer nothing
       about WHICH of three garments is on the line. Here the job is the
       opposite of an ad's — not to stop a scroll but to confirm an object
       already chosen — so here the garment-alone frame is the right one.

       HOW IT IS FOUND. /cart.js carries only featured_image, so the frame is
       looked up from /products/<handle>.js and cached per handle for the life
       of the page. The corgi is painted immediately so the row is never
       blank, and swapped when the lookup lands — one small request per
       distinct product, once. Keyed on the alt the import writes ('shown on
       its own'), with 'not the worn shot' as the fallback, so a renamed file
       cannot break it.

       Same treatment for the cross-sell thumbnails below, where the product
       JSON is already in hand and no request is needed at all. */
    var img = item.featured_image && item.featured_image.url
      ? '<img src="' + esc(thumb(item.featured_image.url, 160)) +
        '" alt="' + esc(item.featured_image.alt || item.product_title) + '" width="72" height="90" loading="lazy"' +
        (item.handle ? ' data-cart-thumb="' + esc(item.handle) + '"' : '') + '>'
      : '';

    /* THE OLD PRICE IS AN <s> WITH A LABEL, and the space in front of it is
       load-bearing. The bug WAS a bare span glued to the final price: measured
       textContent of .cooper-cart__price on a line whose original was 800,00 and
       final 600,00 read '600,00 Kc800,00 Kc', which a screen reader speaks as
       one run - six hundred crowns eight hundred crowns, with nothing to say
       which is which. Sighted readers were fine because the span is styled
       line-through, but the styling carried the whole meaning.
       <s> plus a visually-hidden 'was ' is what
       sections/cooper-cart-page.liquid:180 already does on the cart page, so the
       two surfaces now read the same. */
    /* THE HIGHER OF THE TWO ANCHORS. A cart-level discount and a compare-at
       price are different claims and a line can carry both; striking the larger
       is the only figure that is true of the whole line. */
    var anchorLine = anchorCents(item) * item.quantity;
    var wasCents = Math.max(item.original_line_price, anchorLine);
    var was = wasCents > item.final_line_price
      ? ' <s class="cooper-cart__was"><span class="cooper-cart__sr">was </span>' +
        money(wasCents) + '</s>'
      : '';

    /* NO CHIP ON THE LINE, DELIBERATELY, AND IT WAS HERE FOR AN HOUR.

       Buy-X-Get-Y puts its whole reduction on the "get" line, so a chip beside
       that line reads as though one garment were on offer and the other was not
       -- which is the very thing the owner had just asked about ("почему скидка
       падает только на 1 товар"). Saying the figure next to the line that
       happens to carry the allocation makes that misreading louder.

       The struck price still says this line changed. What the ORDER saves is
       stated once, above the subtotal, where the money is actually decided. */

    return (
      '<li class="cooper-cart__item" data-line="' + (index + 1) + '">' +
      '<span class="cooper-cart__thumb">' + img + '</span>' +
      /* THE THREE COLUMNS ARE THEIRS: photograph, then everything about the
         item including the stepper, then a column holding the cross above the
         price. The owner asked for it twice — photo left, price right. */
      '<div class="cooper-cart__lines">' +
      '<p class="cooper-cart__name"><a href="' + esc(item.url) + '">' + esc(item.product_title) + '</a></p>' +
      (item.variant_title && item.variant_title !== 'Default Title'
        ? '<p class="cooper-cart__props"><span>' + esc(item.variant_title) + '</span></p>'
        : '') +
      (props ? '<p class="cooper-cart__props">' + props + '</p>' : '') +
      '<div class="cooper-cart__qty">' +
      '<button type="button" data-step="-1" aria-label="One fewer ' + esc(item.product_title) + '">&minus;</button>' +
      '<output aria-label="Quantity">' + item.quantity + '</output>' +
      '<button type="button" data-step="1" aria-label="One more ' + esc(item.product_title) + '">+</button>' +
      '</div>' +
      '</div>' +
      '<div class="cooper-cart__right">' +
      '<button type="button" class="cooper-cart__remove" data-remove aria-label="Remove ' + esc(item.product_title) + '">' +
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true" focusable="false">' +
      '<path d="M6 6l12 12M18 6L6 18" /></svg></button>' +
      '<p class="cooper-cart__price">' + money(item.final_line_price) + was + '</p>' +
      '</div>' +
      '</li>'
    );
  }

  /* HOW MANY GARMENTS, WHICH IS NOT HOW MANY LINES.

     cart.item_count counts the embroidery line, so a cap and a bandana read as
     three and the number went UP the moment a word was typed -- the buyer sees
     a count of things he chose, and he chose two. The line itself stays visible
     in the list at 0,00: excluded from the tally, not hidden from the buyer.

     KEYED ON THE TYPE IT REALLY HAS. The comment further down this file claimed
     the add-on carries no product_type; queried on the live shop, Embroidery is
     productType "Add-on". The upsell rule survived that because "Add-on"
     matches none of the three garment types, which is exactly how a false
     premise sits undisturbed under working code. */
  function garmentCount() {
    var n = 0;
    for (var i = 0; i < cart.items.length; i++) {
      var t = cart.items[i].product_type || '';
      if (CHARGE_TYPES.indexOf(t.toLowerCase()) === -1) n += cart.items[i].quantity;
    }
    return n;
  }

  /* THE SENTENCE A SCREEN READER HEARS, from the locale rather than from English
     typed into a script: Ella publishes the pluralised templates for this. */
  function countSentence(n) {
    var s = window.cartStrings || {};
    var t = n === 1 ? s.item : s.items;
    if (!t) return n + (n === 1 ? ' item' : ' items');
    return String(t).replace('[count]', n);
  }

  /* Every one of them in the document, not just the drawer's own: the header,
     the mobile toolbar and Ella's drawer each render one, all frozen at load.
     The icon's own aria-label is the same string and goes stale the same way, so
     it is rewritten from the same sentence. */
  function paintCountText(n) {
    var said = countSentence(n);
    var nodes = document.querySelectorAll('[data-cart-text]');
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].textContent !== said) nodes[i].textContent = said;
      var link = nodes[i].closest('[aria-label]');
      if (link && link.getAttribute('aria-label') !== said) {
        link.setAttribute('aria-label', said);
      }
    }
  }

  function paint() {
    if (!cart) return;
    var count = cart.item_count;
    var shown = garmentCount();

    if (countEl) {
      countEl.hidden = shown === 0;
      countEl.textContent = shown ? '(' + shown + ')' : '';
    }

    paintCountText(shown);

    itemsEl.innerHTML = cart.items.map(lineHTML).join('');
    /* Миниатюры показывают предмет, а не корги — см. рассуждение у lineHTML.
       Вызывается сразу после перерисовки строк, чтобы подмена попала в тот же
       кадр, где ящик уже нарисован. */
    paintGarmentThumbs(itemsEl);
    ensureAnchors(cart.items);
    itemsEl.hidden = count === 0;
    if (emptyEl) emptyEl.hidden = count > 0;
    if (footEl) footEl.hidden = count === 0;

    if (subtotalEl) subtotalEl.textContent = money(cart.total_price);

    /* WHAT THE WHOLE ORDER SAVES, which the per-line chips cannot say on their
       own. Buy-X-Get-Y puts its entire reduction on the "get" line -- a duo shows
       6,90 off the bandana and nothing on the cap -- so line by line it reads as
       though one garment was discounted and the other was not. cart.total_discount
       is the same figure the checkout prints as TOTAL SAVINGS.

       HIDDEN AT ZERO rather than printed as a nought: a row saying you saved
       nothing is worse than no row. */
    if (savedEl) {
      /* WHAT THE WHOLE BASKET SAVES = the reduction already in the shelf price
         PLUS whatever a code took off on top. Before this it was only the
         second half, so a basket of two reduced garments and no code announced
         a saving of nothing. */
      var shelfSaved = 0;
      for (var si = 0; si < cart.items.length; si++) {
        var it = cart.items[si];
        var a = anchorCents(it) * it.quantity;
        if (a > it.original_line_price) shelfSaved += a - it.original_line_price;
      }
      var savedTotal = (cart.total_discount || 0) + shelfSaved;
      savedEl.hidden = savedTotal <= 0;
      var amountEl = savedEl.querySelector('[data-cart-saved-amount]');
      if (amountEl) amountEl.textContent = money(savedTotal);
    }

    if (checkoutBtn && checkoutLabel) {
      var template = checkoutBtn.dataset.label || 'Checkout';
      checkoutLabel.textContent = template.indexOf('{total}') === -1
        ? template
        : template.replace('{total}', money(cart.total_price));
    }

    paintReward();
    if (count > 0) fillUpsell();
    else if (upsellEl) upsellEl.hidden = true;

    /* -----------------------------------------------------------------------
       AND THE REST OF THE PAGE IS TOLD, because the drawer is not the only
       thing on screen showing what is in the cart.

       The set builder on a product page prints `In your cart` under each card.
       A buyer who removed the cap here left that card still claiming the cap --
       the drawer emitted nothing, so nothing could listen. Owner's report,
       23.09.2026, with the drawer showing one hat beside two ticked cards.

       Announced from paint() rather than from the three mutation sites: paint()
       is the one place every change passes through -- add, quantity, removal,
       the orphan tidy-up, and the plain refresh on open -- so a future fourth
       path is covered without anyone remembering to add a line. It carries the
       cart it just drew, so a listener need not fetch /cart.js again.
       --------------------------------------------------------------------- */
    document.dispatchEvent(new CustomEvent('cooper:cart:changed', {detail: {cart: cart}}));
  }

  /* -------------------------------------------------------------------------
     THE REWARD BAR.

     Its arithmetic is the cart total against one threshold, both in cents and
     both in the SAME currency — which is the only reason this is safe to
     compute here at all. `cart.total_price` is in the presentment currency of
     the visitor's market, so the threshold is converted to it by RATE above.
     Never mix the figures typed into the home page in: those are euros and
     stay euros whatever market the visitor is in.
     ---------------------------------------------------------------------- */
  function paintReward() {
    if (!rewardEl || THRESHOLD <= 0) return;
    var total = cart.total_price || 0;
    rewardEl.hidden = cart.item_count === 0;
    var pct = Math.max(0, Math.min(100, (total / THRESHOLD) * 100));
    if (barEl) barEl.style.width = pct + '%';

    var reached = total >= THRESHOLD;
    if (reached) rewardEl.setAttribute('data-reached', '');
    else rewardEl.removeAttribute('data-reached');

    var progressTemplate = rewardEl.dataset.progress || root.dataset.rewardProgress || '{amount} away from free shipping';
    var reachedText = rewardEl.dataset.reached || root.dataset.rewardReached || 'Free shipping unlocked';
    /* THE AMOUNT IS BOLD INSIDE THE SENTENCE, theirs — it is the one number
       somebody reads twice. innerHTML rather than textContent because of that,
       and the only thing interpolated is money() output, which is built from a
       number and the shop's own format string. The aria-label takes the plain
       text so a screen reader hears a sentence and not markup. */
    var plain = reached
      ? reachedText
      : progressTemplate.replace('{amount}', money(THRESHOLD - total));
    if (rewardText) {
      rewardText.innerHTML = reached
        ? esc(plain)
        : esc(progressTemplate).replace(
            esc('{amount}'),
            '<strong>' + esc(money(THRESHOLD - total)) + '</strong>'
          );
    }
    if (barWrap) barWrap.setAttribute('aria-label', plain);
  }

  /* -------------------------------------------------------------------------
     THE UPSELL.

     Shopify's recommendations endpoint takes ONE product id, so the seed is the
     first line in the cart — the same choice the reference makes. Anything
     already in the cart is filtered out, because suggesting what somebody has
     just bought is the failure mode that makes an upsell read as spam.
     ---------------------------------------------------------------------- */
  var upsellFor = null;

  /* -------------------------------------------------------------------------
     WHAT TO OFFER, AND WHY IT IS NOT SHOPIFY'S RECOMMENDATIONS ANY MORE.

     It used to call /recommendations/products.json seeded on the first line in
     the cart. On a shop of thirty-five near-identical garments that answers with
     more of the same thing: a buyer holding a navy cap was shown four other
     caps. `intent=complementary` would be the right endpoint, but complementary
     products are set per product by hand in Shopify's Search & Discovery app,
     all thirty-five of them, and none are set.

     THE RULE THE OWNER ASKED FOR, in his words: a cap in the cart, show a neck
     scarf; a bucket hat, show a neck scarf; a cap and a scarf, show bucket hats.
     Generalised, that is: OFFER THE GARMENT TYPE THE CART DOES NOT HAVE YET, in
     a fixed order of preference. Scarves first because they are the cheapest
     thing to add to anything, then bucket hats, then caps.

     IT DEGRADES TO WHAT EXISTS — and as of 26 August all three categories are
     stocked, so this is no longer the branch being taken. Formerly: the
     scarf branch finds nothing and the rule moves to the next missing type
     instead of showing an empty carousel. When the scarf photography lands the
     same code starts offering them with no change here.

     PRICES ARRIVE IN A DIFFERENT UNIT FROM THE OLD ENDPOINT, and this is the
     trap that would have printed 3490 euro. /recommendations/products.json gives
     price in CENTS as an integer; /collections/<handle>/products.json gives it as
     a DECIMAL STRING, "34.90". money() takes cents, so a string with a dot in it
     is multiplied by a hundred and anything else is passed through as it is.
     ---------------------------------------------------------------------- */
  /* BOTH HALVES OF THE FIRST ENTRY MOVED ON 26 AUGUST, and the failure would
     have been silent. "Neck scarf" became "Bandana" across the shop: the tag,
     the product_type, the titles and the collection handle. This list compares
     item.product_type against `type`, so the stale string would have matched
     nothing and the cart would simply never have offered a bandana — and the
     branch below is written to degrade to whatever exists, so nothing would have
     thrown and nobody would have noticed. The handle half would have survived on
     the 301 redirect, which is exactly what makes the type half dangerous: half
     of it still working is how a bug hides. */
  /* AND product_type IS TRANSLATED, which is the second half of the same
     lesson. /de/products/navy-cap.js answers type "Kappe"; these three English
     words matched none of it, so on the German storefront nothing was ever
     suppressed and the drawer offered a cap to somebody already holding a cap.
     Measured 30.08.2026.

     The section now hands over what each category is called in the language
     being served, read off the shop itself -- see sections/cooper-cart.liquid
     -- and the English list below is what is used if it cannot. ORDER IS
     PRESERVED from the attribute, because it is the order the suggestions are
     offered in. */
  var UPSELL_ORDER = (function () {
    var fallback = [
      {type: 'Bandana', handle: 'bandanas'},
      {type: 'Bucket Hat', handle: 'bucket-hats'},
      {type: 'Cap', handle: 'caps'}
    ];
    var raw = root.dataset.upsellTypes || '';
    if (!raw) return fallback;
    var out = [];
    raw.split('|').forEach(function (pair) {
      var at = pair.indexOf('=');
      if (at < 1) return;
      var handle = pair.slice(0, at).trim();
      var type = pair.slice(at + 1).trim();
      if (handle && type) out.push({type: type, handle: handle});
    });
    /* A partly-filled list is worse than the fallback: a category whose
       collection happened to be empty would be missing its type and would then
       be suggested for ever. */
    return out.length === fallback.length ? out : fallback;
  })();

  function cents(value) {
    if (typeof value === 'number') return value;
    var text = String(value == null ? '' : value);
    if (text.indexOf('.') !== -1) return Math.round(parseFloat(text) * 100);
    var n = parseInt(text, 10);
    return isNaN(n) ? 0 : n;
  }

  /* The cart line carries product_type on this shop because every product was
     created with one.

     AND THE ADD-ON HAS ONE TOO — this comment used to say it had none. Queried
     on the live shop: Embroidery is productType "Add-on". The rule below was
     right anyway, because "Add-on" matches none of the three garment types, so
     the wrong reason and the right answer travelled together. Named here
     because garmentCount() above now depends on that type existing, and a fix
     resting on "it has no type" would have been resting on nothing. */
  function typesInCart() {
    var seen = {};
    for (var i = 0; i < cart.items.length; i++) {
      var t = cart.items[i].product_type;
      if (t) seen[t] = true;
    }
    return seen;
  }

  /* THE SIZE THE BUYER IS ALREADY BUYING, read off the garment lines and not
     the charge lines. options_with_values is the reliable read -- variant_title
     is a joined string that would need parsing the day a second option exists --
     and the fallback covers a cart JSON that predates it.

     THE LAST GARMENT WINS. A cart holding an L and an S is somebody buying for
     two heads, and the piece being added belongs to the head in mind now. */
  function sizeInCart() {
    var found = '';
    for (var i = 0; i < cart.items.length; i++) {
      var item = cart.items[i];
      var t = (item.product_type || '').toLowerCase();
      if (CHARGE_TYPES.indexOf(t) !== -1) continue;
      var opts = item.options_with_values || [];
      var here = '';
      for (var k = 0; k < opts.length; k++) {
        if (String(opts[k].name || '').toLowerCase() === 'size') here = opts[k].value;
      }
      if (!here && item.variant_title && item.variant_title !== 'Default Title') {
        here = String(item.variant_title).split('/')[0].trim();
      }
      if (here) found = here;
    }
    return found;
  }

  /* The wanted size if it can be had, the first available one otherwise. Never
     nothing: a suggestion the buyer cannot add is worse than one in the wrong
     size, so the size is a preference and availability is the rule. */
  function pickVariant(variants, wanted) {
    var list = (variants || []).filter(function (v) { return v.available; });
    if (!list.length) return null;
    if (wanted) {
      var want = String(wanted).toLowerCase();
      for (var i = 0; i < list.length; i++) {
        var title = String(list[i].title || '').toLowerCase();
        if (title === want || String(list[i].option1 || '').toLowerCase() === want) {
          return list[i];
        }
      }
    }
    return list[0];
  }

  function fillUpsell() {
    if (!upsellEl || !UPSELL_LIMIT || !cart.items.length) return;

    var have = typesInCart();
    var wanted = [];
    for (var i = 0; i < UPSELL_ORDER.length; i++) {
      if (!have[UPSELL_ORDER[i].type]) wanted.push(UPSELL_ORDER[i]);
    }
    if (!wanted.length) {
      /* Every type is already in the cart, so there is nothing honest to
         suggest. Hide rather than repeat something they are holding. */
      upsellEl.hidden = true;
      return;
    }

    /* The signature is what stops a refetch on every cart read: it changes only
       when the SET of types in the cart changes, which is exactly when the
       suggestion should change. */
    /* THE SIZE IS PART OF THE SIGNATURE. Without it, changing the cap in the
       cart from S to L leaves the set of missing types identical, the signature
       unchanged, and the suggested bandana still in S -- the exact bug this
       block was built to avoid, one field further along. */
    var wantSize = sizeInCart();
    var signature = wanted.map(function (w) { return w.handle; }).join(',') +
      '|' + cart.items.length + '|' + wantSize;
    if (upsellFor === signature) return;
    upsellFor = signature;

    var inCart = cart.items.map(function (i) { return i.product_id; });

    var tryNext = function (index) {
      if (index >= wanted.length) {
        upsellEl.hidden = true;
        return;
      }
      var target = wanted[index];
      var query = url('/collections/' + target.handle + '/products.json') +
        '?limit=' + (UPSELL_LIMIT + cart.items.length + 4);

      fetch(query, {headers: {Accept: 'application/json'}})
        .then(function (r) { return r.ok ? r.json() : {products: []}; })
        .then(function (data) {
          var list = (data.products || []).filter(function (p) {
            if (inCart.indexOf(p.id) !== -1) return false;
            var vs = p.variants || [];
            for (var k = 0; k < vs.length; k++) {
              if (vs[k].available) return true;
            }
            return false;
          }).slice(0, UPSELL_LIMIT);

          if (!list.length) {
            /* This type has nothing purchasable -- the empty scarf collection
               today -- so fall through to the next missing type. */
            tryNext(index + 1);
            return;
          }

          /* Подписи берутся из разметки ящика, а не из этого файла: у секции
             есть локализованные строки, а у скрипта нет. */
          var addLabel = (root.getAttribute('data-add-label') || 'Add').trim();
          var chooseLabel = (root.getAttribute('data-choose-label') || 'Choose a size').trim();
          upsellList.innerHTML = list.map(function (p) {
            var variant = pickVariant(p.variants, wantSize);
            if (!variant) return '';
            /* Предмет, а не корги — см. рассуждение у миниатюры строки. Здесь
               product JSON уже загружен, поэтому кадр берётся из него без
               единого лишнего запроса. */
            /* COLLECTION JSON CARRIES ALTS ON NOTHING, so garmentShot cannot
               work here and the frame is taken by POSITION instead. Measured on
               /collections/bucket-hats/products.json: images is an array of
               objects with src, width and position — and no alt at all, unlike
               /products/<handle>.js which has media[].alt.

               Position 2 is the garment-alone shot on all 63 listings as of
               4 September 2026, because position 1 was moved to the corgi that
               morning. That is a weaker key than an alt and it is written down
               as such: if the media order changes again, this line follows the
               order, not the meaning. It degrades to the featured image, which
               is a picture of the right product either way.

               The cart LINE thumbnails do it properly by alt — see garmentShot
               and paintGarmentThumbs — because /products/<handle>.js gives them
               the media array. Here the cheap request is deliberately not made:
               the upsell draws up to three cards and three extra fetches on the
               way to checkout is not worth an alt. */
            var imgs2 = (p.images && p.images.length) ? p.images : [];
            var pick2 = imgs2.length > 1 ? imgs2[1] : imgs2[0];
            var src = (pick2 && (pick2.src || pick2)) || p.featured_image || '';
            var img = src
              ? '<img src="' + esc(thumb(String(src), 120)) +
                '" alt="" width="58" height="58" loading="lazy">'
              : '';
            return (
              '<li class="cooper-cart__up">' +
              '<span class="cooper-cart__up-thumb">' + img + '</span>' +
              '<div>' +
              '<p class="cooper-cart__up-name">' + esc(p.title) + '</p>' +
              /* THE REDUCTION HERE TOO. Owner, 28.09: the cross-sell printed
                 725,00 Kc flat while the line above it in the same drawer
                 printed 725 struck through 1 025. The anchor needs no request:
                 this card is built from /products/<handle>.js, which carries
                 compare_at_price per variant — unlike /cart.js, which carries
                 no anchor at all. */
              '<p class="cooper-cart__up-price">' + money(cents(variant.price)) +
                (cents(variant.compare_at_price) > cents(variant.price)
                  ? ' <s class="cooper-cart__was">' + money(cents(variant.compare_at_price)) + '</s>'
                  : '') +
              '</p>' +
              '</div>' +
              /* ОДНО НАЖАТИЕ СНОВА ДОБАВЛЯЕТ, но не так, как в первый раз.

                 Почему сначала сделали ссылку, 4 сентября 2026: кнопка постила
                 {items:[{id, quantity:1}]} с variant от pickVariant(), который
                 при пустом предпочтении отдавал ПЕРВЫЙ доступный вариант — то
                 есть размер S. Ящик клал на чекаут изделие под заказ в самом
                 маленьком из пяти отдельно отрисованных лекал.

                 Почему кнопка вернулась 6 сентября по решению владельца: ссылка
                 уводила ИЗ корзины на страницу товара, то есть чинила размер
                 ценой ухода с чекаута. Дефект был не в кнопке, а в том, откуда
                 бралась вариация.

                 Теперь размер наследуется из корзины: sizeInCart() читает опцию
                 Size у уже лежащих строк, pickVariant() ищет её среди доступных
                 вариантов предложения. Человек, у которого в корзине L, получит
                 L, а не S. Именно этого не хватало исходной кнопке.

                 ССЫЛКА ОСТАЁТСЯ РОВНО В ОДНОМ СЛУЧАЕ: наследовать нечего
                 (в корзине только надстройка без размера), а у товара
                 несколько доступных вариантов. Гадать между ними нельзя, а
                 подставить первый — это и есть исходная ошибка с размером S.
                 Когда вариант один, гадать не о чем и кнопка остаётся кнопкой.

                 Чего кнопка по-прежнему НЕ делает: не даёт написать надпись.
                 В панели ноль полей ввода, замерено. Предмет уедет без вышивки,
                 и это осознанный размен — владелец выбрал скорость чекаута. */
              (!wantSize && (p.variants || []).filter(function (v) { return v.available; }).length > 1
                ? '<a class="cooper-cart__up-add" href="' + esc(url('/products/' + p.handle)) +
                  '">' + esc(chooseLabel) + '</a>'
                : '<button type="button" class="cooper-cart__up-add" data-add="' +
                  esc(String(variant.id)) + '">' + esc(addLabel) + '</button>') +
              '</li>'
            );
          }).join('');
          upsellEl.hidden = false;
          syncNav();
          syncNavSoon();
        })
        .catch(function () {
          tryNext(index + 1);
        });
    };

    tryNext(0);
  }

  /* -------------------------------------------------------------------------
     THE CAROUSEL'S ARROWS.

     One card per press, and the card's own width is measured rather than
     assumed: the track is grid-auto-columns minmax(90%, calc(100% - 28px)), so
     the step changes with the panel width and any typed figure here would be
     wrong at one of them. `scroll-behavior: smooth` in the stylesheet does the
     animation, and a reduced-motion visitor gets the jump instead.

     THE BUTTONS DISABLE AT THE ENDS. A press that does nothing reads as a broken
     control, and with one card visible out of three that is two thirds of the
     presses. The 2px slack absorbs sub-pixel scroll positions, which otherwise
     leave the last card one hundredth of a pixel short of the end forever.
     ---------------------------------------------------------------------- */
  function step() {
    var card = upsellList && upsellList.querySelector('.cooper-cart__up');
    if (!card) return 0;
    return card.getBoundingClientRect().width + 10;
  }

  function syncNav() {
    if (!upsellEl) return;
    var prev = upsellEl.querySelector('[data-up-prev]');
    var next = upsellEl.querySelector('[data-up-next]');
    if (!prev || !next || !upsellList) return;
    var max = upsellList.scrollWidth - upsellList.clientWidth;
    /* 6px OF SLACK, NOT 2. The bug WAS that 2px is sub-pixel slack while the
       error here is a SNAP error: measured with 3 cards in a 440px panel,
       max = 1154 - 440 = 714, and scrolling to the right-hand end SETTLED at
       scrollLeft 711 because the last card carries scroll-snap-align: start - so
       711 >= 712 was false and the next arrow stayed enabled at the end, where a
       press does nothing. Reaching the same end by focusing the last +Add put
       scrollLeft at exactly 714 and the button did disable, so the state
       depended on how the end was reached. 6 covers the measured 3px snap error
       on both ends with room to spare and is still far under one card. */
    prev.disabled = upsellList.scrollLeft <= 6;
    next.disabled = upsellList.scrollLeft >= max - 6;
    /* With nothing to scroll there is nothing to steer. 12 rather than 2: below
       one snap error's worth of travel there is no meaningful scroll to offer. */
    var nav = upsellEl.querySelector('.cooper-cart__upsell-nav');
    if (nav) nav.hidden = max <= 12;
  }

  /* -------------------------------------------------------------------------
     WHEN syncNav HAS TO RUN AGAIN, and getting this wrong disabled the carousel
     completely.

     Measured after the first fix: three cards rendered, max scroll 714, and the
     next arrow was disabled from the moment the drawer opened — so a click did
     nothing, because a disabled button fires no click event at all, and the only
     thing that re-ran syncNav was a scroll event that could therefore never
     happen. A dead end that repaired itself never.

     THE CAUSE IS THAT ONE CALL AT RENDER TIME IS TOO EARLY. The cards are laid
     out, but their PHOTOGRAPHS ARE NOT LOADED, so scrollWidth is momentarily
     within a few pixels of clientWidth. `0 >= max - 6` is true for any max under
     six, which is exactly the window a half-laid-out track sits in — wide enough
     that the nav was not hidden, narrow enough that the arrow was disabled.

     So it runs again on everything that can change the track's width: the next
     frame, every image as it lands, and a resize. Cheap, idempotent, and it
     cannot leave a stale disabled state behind.
     ---------------------------------------------------------------------- */
  function syncNavSoon() {
    if (!upsellList) return;
    requestAnimationFrame(function () {
      requestAnimationFrame(syncNav);
    });
    Array.prototype.forEach.call(upsellList.querySelectorAll('img'), function (img) {
      if (img.complete) return;
      img.addEventListener('load', syncNav, {once: true});
      img.addEventListener('error', syncNav, {once: true});
    });
  }

  if (upsellList) {
    upsellList.addEventListener('scroll', syncNav, {passive: true});
    /* A resize changes the card width, which changes both the step and the end. */
    var navResize;
    window.addEventListener('resize', function () {
      clearTimeout(navResize);
      navResize = setTimeout(syncNav, 150);
    }, {passive: true});
  }

  /* -------------------------------------------------------------------------
     READS AND WRITES.
     ---------------------------------------------------------------------- */
  /* 8 SECONDS, AND IT IS AN UPPER BOUND ON SILENCE, not a performance target.
     /cart.js on this shop answers in tens of milliseconds; the number exists
     because a fetch that never settles produced NOTHING AT ALL - measured with a
     never-resolving /cart.js and the region cleared first: status '', the
     previous 1 row still on screen, the same subtotal '890,00 Kc', no spinner,
     aria-busy null. A visitor cannot tell that from a cart that simply has one
     thing in it. Anything under about 5s would abort a slow-but-working read on
     a bad mobile connection, which is a worse lie than the wait. */
  var READ_TIMEOUT = 8000;
  var READ_FAILED = 'The cart could not be read. Reload the page.';
  var CHANGE_FAILED = 'That did not go through. Try again.';

  function refresh() {
    /* THE 500 USED TO BE INVISIBLE TOO. `return r.json()` with no r.ok check:
       measured with /cart.js forced to a 500 that returns HTML, the parse threw
       and the catch ran by accident, but a 500 that returns valid JSON would
       have been assigned straight into `cart` and painted as if it were a cart.
       Checking the status makes the failure the trigger rather than the shape of
       the body. */
    var ctrl = 'AbortController' in window ? new AbortController() : null;
    var timer = ctrl
      ? setTimeout(function () {
          ctrl.abort();
        }, READ_TIMEOUT)
      : 0;
    var options = {headers: {Accept: 'application/json'}};
    if (ctrl) options.signal = ctrl.signal;

    return fetch(url('/cart.js'), options)
      .then(function (r) {
        if (!r.ok) throw new Error('cart.js ' + r.status);
        return r.json();
      })
      .then(function (data) {
        cart = data;
        clearFailure();
        paint();
        /* EVERY PATH THAT REFRESHES THE CART NOW MOVES THE HEADER NUMBER, and
           putting the call here rather than at three call sites is the whole
           fix. bubble() was invoked from the quantity-change chain, the
           form-submit add and the upsell add — but NOT from refresh(), and
           refresh() is what open() runs. So the one path that matters most
           went unserved: the buy box posts its own {items} to /cart/add.js and
           then dispatches cooper:cart:open, which opens the drawer and
           refreshes — and left the header showing whatever it showed at page
           load.

           Measured 4 September 2026 on /products/amber-cap at 390x844: after a
           successful add /cart.js returned item_count 1 while the header bubble
           read "0". A buyer who closes the drawer then has a shop that says his
           cart is empty.

           The three existing calls are left where they are. bubble() only reads
           `cart` and writes text, so calling it twice on one interaction costs
           nothing and removing them would be a second change with no gain. */
        bubble();
        return data;
      })
      .catch(function () {
        /* One sentence for every way this can go wrong - a status, a body that
           is not JSON, a network that dropped, the abort above. None of them
           tell the visitor anything they can act on except reloading. */
        fail(READ_FAILED);
      })
      .then(function (data) {
        if (timer) clearTimeout(timer);
        return data;
      });
  }

  /* -------------------------------------------------------------------------
     FOCUS HAS TO SURVIVE THE REPAINT.

     paint() replaces the whole list with one innerHTML write, which destroys the
     button that was pressed. Measured with the inert bug lifted so focus could
     be read at all: focus on the plus button of line 1 (confirmed focused),
     click, document.activeElement is BODY - the same after Remove. A keyboard or
     screen-reader visitor was thrown to the top of the document on every
     quantity press, had to tab all the way back in, and because focus had left
     the panel the Tab trap in onKey() had nothing left to trap.

     The pressed control is remembered as a SELECTOR rather than as a node,
     because the node itself will not exist after the repaint: the line number
     plus the control's own data attribute. When that row is gone - a removal, or
     a quantity taken to zero - focus falls back to the panel's close button,
     which is the one control that is always there.
     ---------------------------------------------------------------------- */
  function focusTarget() {
    var el = document.activeElement;
    if (!el || !itemsEl || !itemsEl.contains(el)) return null;
    var row = el.closest('[data-line]');
    if (!row) return null;
    var within = el.hasAttribute('data-step')
      ? '[data-step="' + el.dataset.step + '"]'
      : (el.hasAttribute('data-remove') ? '[data-remove]' : null);
    if (!within) return null;
    return '[data-line="' + row.dataset.line + '"] ' + within;
  }

  function restoreFocus(selector) {
    if (!selector) return;
    var again = itemsEl && itemsEl.querySelector(selector);
    if (again && again.focus) {
      again.focus();
      return;
    }
    /* NOT THE SCRIM, which also carries [data-cart-close]: it is tabindex="-1"
       and has no visible focus ring, so focus would land somewhere invisible. */
    var x = panel.querySelector('[data-cart-close]:not([tabindex="-1"])');
    if (x && x.focus) x.focus();
  }

  /* -------------------------------------------------------------------------
     THE CHARGE LINE MUST NOT OUTLIVE THE WORDS.

     Remove the cap and the free Embroidery line used to stay behind, with its
     own stepper and its own Remove button, still naming the thread of a garment
     that had left the cart. Both add paths get the line right on the way in --
     one per order, only when something is written -- but nothing looked at it
     again when a garment went out, because only an add had ever created it.

     THE TEST IS THE ADD PATHS' RULE READ BACKWARDS: the charge exists if
     anything is written, so when nothing is written any more it goes. That also
     covers the case a "was a garment removed" test would miss -- clearing the
     words off the last written piece while keeping the piece.

     WRITTEN MEANS A PROPERTY THAT IS NOT THE THREAD. The thread is a property of
     the words; a line carrying only a colour is not written on.

     Returns the 1-based line of the charge to drop, or 0 for nothing to do.
     ---------------------------------------------------------------------- */
  function orphanChargeLine() {
    if (!cart || !cart.items) return 0;
    var written = 0;
    var charge = 0;
    for (var i = 0; i < cart.items.length; i++) {
      var item = cart.items[i];
      var type = (item.product_type || '').toLowerCase();
      if (CHARGE_TYPES.indexOf(type) !== -1) {
        charge = i + 1;
        continue;
      }
      var props = item.properties || {};
      for (var key in props) {
        if (!Object.prototype.hasOwnProperty.call(props, key)) continue;
        if (String(key).toLowerCase() === 'thread') continue;
        if (props[key] && String(props[key]).trim()) {
          written += 1;
          break;
        }
      }
    }
    return written === 0 ? charge : 0;
  }

  function change(line, quantity, announce) {
    if (busy) return;
    busy = true;
    root.setAttribute('aria-busy', 'true');
    var refocus = focusTarget();
    fetch(url('/cart/change.js'), {
      method: 'POST',
      headers: {'Content-Type': 'application/json', Accept: 'application/json'},
      body: JSON.stringify({line: line, quantity: quantity})
    })
      /* THE RESPONSE IS CHECKED BEFORE IT BECOMES THE CART, which add() below
         has always done and this did not. The bug WAS `return r.json()` followed
         by `cart = data`: measured with /cart/change.js answering 422
         {description: 'You can only add 5 of Bandana to the cart.'} the way
         Shopify does on an inventory cap, the first press of + showed the
         generic 'That did not go through. Try again.' (Shopify's own sentence
         thrown away) and the quantity did not move, while the 422 body - which
         has no `items` - was assigned into `cart`. The second press then threw
         'Uncaught TypeError: Cannot read properties of undefined (reading 0)'
         out of `cart.items[line - 1]` and the whole panel was dead: Remove threw
         the same, the row count never changed, and only reopening fixed it.

         AN INVENTORY CAP IS AN ORDINARY 422, not an outage - it is the shop
         working correctly - so the visitor gets Shopify's own description, which
         names the product and the number. */
      .then(function (r) {
        return r.json().then(
          function (d) {
            if (!r.ok) throw new Error(d.description || d.message || CHANGE_FAILED);
            return d;
          },
          function () {
            /* A body that is not JSON at all: an outage page, a proxy's 502.
               There is nothing quotable in it, so the generic line stands. */
            throw new Error(CHANGE_FAILED);
          }
        );
      })
      .then(function (data) {
        cart = data;
        /* AND IF THAT REMOVAL ORPHANED THE CHARGE LINE, TAKE IT WITH IT.

           Inside the same chain and the same `busy` window on purpose: no second
           click can interleave, and this cannot trigger itself, because once the
           charge line is gone there is nothing left for it to find.

           A failure here is deliberately swallowed. The buyer's own removal has
           already succeeded; refusing to paint it because a tidy-up did not go
           through would turn a cosmetic leftover into a broken panel. */
        var orphan = orphanChargeLine();
        if (!orphan) return null;
        return fetch(url('/cart/change.js'), {
          method: 'POST',
          headers: {'Content-Type': 'application/json', Accept: 'application/json'},
          body: JSON.stringify({line: orphan, quantity: 0})
        })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (d) { if (d && d.items) cart = d; })
          .catch(function () { /* the removal stands; the leftover can wait */ });
      })
      .then(function () {
        /* THE SEED IS CLEARED so the upsell is asked again: removing the last
           bucket hat changes what goes with what. */
        if (!cart.items.length || (cart.items[0] && cart.items[0].product_id !== upsellFor)) upsellFor = null;
        paint();
        restoreFocus(refocus);
        clearFailure();
        if (announce) say(announce);
        bubble();
      })
      .catch(function (err) {
        /* fail(), not say(): the cap message is the one sentence that explains
           why the number on screen did not move, and in the sr-only region a
           sighted visitor never saw it. */
        fail(String(err.message || err));
      })
      .then(function () {
        busy = false;
        root.removeAttribute('aria-busy');
      });
  }

  function add(variantId, button) {
    if (busy) return;
    busy = true;
    if (button) button.setAttribute('aria-busy', 'true');
    fetch(url('/cart/add.js'), {
      method: 'POST',
      headers: {'Content-Type': 'application/json', Accept: 'application/json'},
      body: JSON.stringify({items: [{id: Number(variantId), quantity: 1}]})
    })
      .then(function (r) {
        return r.json().then(function (data) {
          if (!r.ok) throw new Error(data.description || data.message || 'add failed');
          return data;
        });
      })
      .then(function () {
        upsellFor = null;
        return refresh();
      })
      .then(function () {
        /* open() BEFORE say(), the order the form handler below already uses:
           open() resets the live region (see clearFailure), so a sentence said
           before it would be thrown away. Today this path is only reachable from
           the upsell inside an already-open drawer, where open() returns
           immediately - the order is here so that stops being load-bearing. */
        bubble();
        open();
        say('Added.');
      })
      .catch(function (err) {
        say(String(err.message || err));
      })
      .then(function () {
        busy = false;
        if (button) button.removeAttribute('aria-busy');
      });
  }

  /* ELLA'S OWN CART COUNT STILL HAS TO MOVE. Its bubble is rendered by the
     theme and updated by the theme's script, which this drawer bypasses — so
     the number in the header would freeze at whatever it was on page load.
     Writing the count straight into its node is cheaper and more predictable
     than firing Ella's own events, which are undocumented. */
  /* AND THE NODE IS MADE IF IT IS NOT THERE, which is the whole fix. The bug
     WAS that snippets/cart-icon-bubble.liquid - and snippets/header-functions
     .liquid:191, which is the copy this header uses - render that div only
     inside {%- if cart != empty -%}, so on an EMPTY server cart the node this
     writes into does not exist at all: measured,
     document.querySelectorAll(BUBBLE_SELECTOR).length = 0 while the drawer's own
     count showed '(4)'. That is the first add on an empty cart - the one add
     that matters most - and the header number stayed blank through it.
     #cart-icon-bubble is the span Ella already renders inside the header cart
     link and is where Ella puts this same div itself, so the positioning context
     is identical and no CSS is needed. */
  var BUBBLE_SELECTOR = '.cart-count-bubble span[aria-hidden="true"], [data-cart-count-bubble], .header__cart-count';

  function bubble() {
    if (!cart) return;
    /* THE SAME TALLY AS THE DRAWER. Two counts for one cart is how the header
       and the panel come to disagree in front of the buyer -- and this is the
       one the header shows, so it is the one that has to be right. */
    var count = garmentCount();
    var nodes = document.querySelectorAll(BUBBLE_SELECTOR);
    if (!nodes.length) {
      Array.prototype.forEach.call(document.querySelectorAll('#cart-icon-bubble'), function (host) {
        if (host.querySelector('.cart-count-bubble')) return;
        var made = document.createElement('div');
        made.className = 'cart-count-bubble';
        made.setAttribute('data-cooper-bubble', '');
        var inner = document.createElement('span');
        inner.setAttribute('aria-hidden', 'true');
        made.appendChild(inner);
        host.appendChild(made);
      });
      nodes = document.querySelectorAll(BUBBLE_SELECTOR);
    }
    Array.prototype.forEach.call(nodes, function (n) {
      n.textContent = count;
    });
    /* ZERO SHOWS NOTHING, because that is what the theme does: Ella renders no
       bubble at all on an empty cart, so a '0' badge would be a state the header
       has no style for. Only the div this made is touched - a bubble Ella
       rendered is Ella's to remove.

       THE COUNT IS VISUAL ONLY. Ella's screen-reader sentence beside it comes
       from 'content.cart_count' | t and cannot be rebuilt in JS without the
       translation, so it is left alone rather than written in one language; the
       announcement a visitor needs is the drawer's own live region. */
    Array.prototype.forEach.call(document.querySelectorAll('[data-cooper-bubble]'), function (made) {
      made.style.display = count === 0 ? 'none' : '';
    });
  }

  /* -------------------------------------------------------------------------
     WIRING.
     ---------------------------------------------------------------------- */
  root.addEventListener('click', function (e) {
    if (e.target.closest('[data-cart-close]')) {
      close();
      return;
    }

    var nav = e.target.closest('[data-up-prev], [data-up-next]');
    if (nav && upsellList) {
      upsellList.scrollBy({left: nav.hasAttribute('data-up-next') ? step() : -step(), behavior: 'smooth'});
      return;
    }

    var addBtn = e.target.closest('[data-add]');
    if (addBtn) {
      add(addBtn.dataset.add, addBtn);
      return;
    }

    var row = e.target.closest('[data-line]');
    if (!row) return;
    var line = parseInt(row.dataset.line, 10);
    var item = cart && cart.items[line - 1];

    if (e.target.closest('[data-remove]')) {
      change(line, 0, item ? item.product_title + ' removed' : 'Removed');
      return;
    }
    /* stepBtn, NOT step. The bug WAS a var/function name collision inside this
       one handler: `var step = ...` hoisted a local over the module-level
       `function step()` a dozen lines above, which the arrow branch calls — so
       both carousel arrows threw 'Uncaught TypeError: step2 is not a function'
       and upsellList.scrollLeft stayed 0 (before 0, after 0) on a
       mandatory-snap track where a press is the only way across. The function
       is untouched; only this local was renamed. */
    var stepBtn = e.target.closest('[data-step]');
    if (stepBtn && item) {
      var next = item.quantity + parseInt(stepBtn.dataset.step, 10);
      if (next < 0) return;
      /* A NON-ZERO QUANTITY ANNOUNCES TOO. The bug WAS `: null` here, so
         sections/cooper-cart.liquid:310-316 promised that quantity changes
         announce and only removals did: measured, [data-cart-status] textContent
         was '' after clicking plus and '' again after minus, while a removal did
         say 'Bandana removed'. The <output> holding the number cannot cover for
         it either - paint()'s innerHTML write destroys and re-creates it, and a
         re-created node announces nothing. */
      change(line, next, next === 0
        ? item.product_title + ' removed'
        : item.product_title + ', quantity ' + next);
    }
  });

  /* THE PRODUCT FORM IS INTERCEPTED RATHER THAN REPLACED. The form keeps its
     action and its method, so with this script blocked or broken it still
     posts to /cart/add and the visitor still buys — the drawer is an
     enhancement, not the mechanism. Same reason the checkout button is a real
     submit. */
  document.addEventListener('submit', function (e) {
    var form = e.target.closest('form[action*="/cart/add"]');
    if (!form) return;
    e.preventDefault();
    var submit = form.querySelector('[type="submit"]');
    if (submit) submit.setAttribute('aria-busy', 'true');
    fetch(url('/cart/add.js'), {
      method: 'POST',
      headers: {Accept: 'application/json'},
      body: new FormData(form)
    })
      .then(function (r) {
        return r.json().then(function (data) {
          if (!r.ok) throw new Error(data.description || data.message || 'add failed');
          return data;
        });
      })
      .then(function () {
        upsellFor = null;
        return refresh();
      })
      .then(function () {
        bubble();
        open();
        say('Added.');
      })
      .catch(function (err) {
        /* A FAILED ADD FALLS BACK TO THE REAL FORM rather than telling the
           visitor to try again. Sold out, a variant that vanished, a network
           that dropped: the server's own page says what happened, and it says
           it better than a line in a drawer. */
        say(String(err.message || err));
        form.submit();
      })
      .then(function () {
        if (submit) submit.removeAttribute('aria-busy');
      });
  });

  /* EVERY ROUTE TO THE CART OPENS THE DRAWER, which is what the reference calls
     intercept_cart_links. The <a href="/cart"> stays in the markup and stays a
     real link, so middle-click, ctrl-click and a script-less visit all still
     reach the cart page. */
  document.addEventListener('click', function (e) {
    var link = e.target.closest('a[href$="/cart"], a[href*="/cart?"]');
    if (!link || root.contains(link)) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    open();
  });

  /* The drawer can be opened by anything that asks for it, so the header icon
     and any future button need no knowledge of this file. */
  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-cooper-cart-open]')) {
      e.preventDefault();
      open();
    }
  });

  document.addEventListener('cooper:cart:open', open);

  /* THE SETTINGS THE BAR NEEDS travel on the section element rather than being
     baked into this file, so the owner's wording changes without a deploy. */
  if (rewardEl) {
    rewardEl.dataset.progress = root.dataset.rewardProgress || rewardEl.dataset.progress || '';
    rewardEl.dataset.reached = root.dataset.rewardReached || rewardEl.dataset.reached || '';
  }

  /* One read at load so the header count and the bar are right before anything
     is clicked, and so a cart restored from a previous visit is not a surprise
     the first time the drawer opens. */
  refresh().then(bubble);
})();
